import { Avatar, message, Button, Tooltip } from "antd";
import { CloseOutlined, UsergroupAddOutlined, LoadingOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import "./shareForm.css";
import defaultProfileImage from "../../../assets/defult-profile-logo.png";
import { workwiseServices } from "../../../api";
import { useDocument } from "../../../context/documentInfoContex";
import { useCanvas } from "../../../context/canvasContext";

const RIGHT_LABELS = { 1: "Reader", 2: "Collaborator" };

const PeopleWithAccess = () => {
    const { documentInfo, setDocumentInfo } = useDocument();
    const { userInfo } = useCanvas();
    const [showAll, setShowAll] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    const handleRemoveMember = async (member) => {
        if (removingId) return;
        setRemovingId(member.id);
        try {
            // Api call for remove reader rights for specific user, privacy id "1" represents reader rights
            await workwiseServices.removeDocumentMember({
                documentId: documentInfo?.referenceId,
                status: 1,
                documentMembers: [member.id]
            });

            // Api call for remove collaborator rights for specific user, privacy id "2" represents collaborator rights
            await workwiseServices.removeDocumentMember({
                documentId: documentInfo?.referenceId,
                status: 2,
                documentMembers: [member.id]
            });
            setDocumentInfo({
                ...documentInfo,
                members: documentInfo.members.filter((m) => m.id !== member.id),
            });
        } catch (error) {
            console.error("Error removing document member:", error);
            message.error(error.response?.data?.message || "Failed to remove member.");
        } finally {
            setRemovingId(null);
        }
    };

    const members = documentInfo?.members?.filter(member => member.id !== userInfo?.userId) || [];
    const visibleMembers = showAll ? members : members.slice(0, 3);

    if (members.length === 0) {
        return (
            <div className="doc-member-container">
                <div className="doc-member-header">
                    <p className="share-section-label">People with access</p>
                </div>
                <div className="doc-member-empty">
                    <span className="doc-member-empty__icon">
                        <UsergroupAddOutlined />
                    </span>
                    <span className="doc-member-empty__text">
                        <span className="doc-member-empty__title">Not shared with anyone yet</span>
                        <span className="doc-member-empty__hint">People you invite will appear here.</span>
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="doc-member-container">
            <div className="doc-member-header">
                <p className="share-section-label">People with access</p>
                <span className="doc-member-count">{members.length}</span>
            </div>
            <div className="doc-member-list">
                {visibleMembers.map((member) => {
                    const rightLabel = RIGHT_LABELS[member.rightType ?? member.memberRightType];
                    const isRemoving = removingId === member.id;
                    return (
                    <div key={member.id} className="member-row" data-removing={isRemoving ? "true" : undefined}>
                        <div className="member-row__identity">
                            <Avatar size={28} src={member.image || defaultProfileImage} />
                            <div className="member-row__info">
                                <span className="member-row__name" title={member.name}>{member.name}</span>
                                {member.designation && (
                                    <span className="member-row__designation" title={member.designation}>
                                        {member.designation}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="member-row__meta">
                            {rightLabel && <span className="member-row__right">{rightLabel}</span>}
                            <Tooltip title={isRemoving ? "Removing…" : "Remove access"} placement="left">
                                <button
                                    type="button"
                                    className="member-row__remove"
                                    aria-label={`Remove ${member.name}`}
                                    aria-busy={isRemoving}
                                    disabled={!!removingId}
                                    onClick={() => handleRemoveMember(member)}
                                >
                                    {isRemoving ? <LoadingOutlined /> : <CloseOutlined />}
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                    );
                })}
                {members.length > 3 && (
                    <Button type="link" className="member-toggle-all" onClick={() => setShowAll(!showAll)}>
                        {showAll ? "Show less" : `View all (${members.length})`}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default PeopleWithAccess;
